import { useState } from 'react';
import { Box, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StripeCard from './StripeCard';
import StripeWallets from './StripeWallets';

// Opciones de método de pago. La tarjeta se registra ahora y se cobra al aceptar;
// SPEI/OXXO generan una referencia (CLABE/ficha) cuando el proveedor acepta la reserva.
const METODOS = [
  { key: 'card', label: 'Tarjeta', desc: 'Débito o crédito', icon: CreditCardIcon },
  { key: 'spei', label: 'SPEI', desc: 'Transferencia bancaria', icon: AccountBalanceIcon },
  { key: 'oxxo', label: 'OXXO', desc: 'Efectivo en tienda', icon: StorefrontIcon },
];

const MetodoCard = ({ metodo, activo, onClick }) => {
  const Icono = metodo.icon;
  return (
    <Box
      onClick={onClick}
      role="button"
      sx={{
        flex: 1, minWidth: 120, cursor: 'pointer', px: 2, py: 1.75, borderRadius: '14px',
        border: '2px solid', borderColor: activo ? '#7495F8' : '#E2E8F0',
        bgcolor: activo ? '#F7FAFF' : 'white', transition: 'all .15s',
        display: 'flex', flexDirection: 'column', gap: 0.5,
        '&:hover': { borderColor: activo ? '#7495F8' : '#CBD5E1' },
      }}
    >
      <Icono sx={{ fontSize: 22, color: activo ? '#7495F8' : '#94A3B8' }} />
      <Typography sx={{ fontWeight: 800, color: '#0E2465', fontSize: 14 }}>{metodo.label}</Typography>
      <Typography sx={{ fontSize: 11, color: '#737791' }}>{metodo.desc}</Typography>
    </Box>
  );
};

// Método de pago (Stripe). Tarjeta → campo seguro (se cobra al aceptar). SPEI/OXXO →
// referencia generada al aceptar; el cliente la paga y se confirma automáticamente.
// Apple/Google Pay (wallet) → botón exprés arriba: autoriza y guarda la tarjeta, igual
// que el campo de tarjeta, y crea la reserva en un solo paso (F4B).
const BookingPayment = ({
  paymentMethod = 'card', setPaymentMethod, stripeCardRef,
  onWalletPago, validarPago, onWalletError,
}) => {
  // El botón de wallet solo se muestra si el navegador/dispositivo ofrece Apple/Google Pay.
  const [walletDisponible, setWalletDisponible] = useState(false);

  return (
    <Box sx={{ p: 4, borderRadius: '24px', bgcolor: 'white', border: '1px solid #F1F5F9' }}>
      <Typography variant="h6" fontWeight="900" color="#0E2465" sx={{ mb: 1 }}>
        Método de pago
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <LockIcon sx={{ fontSize: 14, color: '#22C55E' }} />
        <Typography variant="caption" color="#22C55E" fontWeight={600}>
          Pago seguro con Stripe
        </Typography>
      </Box>

      {/* Pago exprés con wallet (Apple/Google Pay). Se monta siempre para poder detectar
          disponibilidad, pero solo ocupa espacio si hay alguna wallet. */}
      {onWalletPago && (
        <Box sx={{ mb: walletDisponible ? 3 : 0 }}>
          <StripeWallets
            validar={validarPago}
            onPago={onWalletPago}
            onError={onWalletError}
            onDisponible={setWalletDisponible}
          />
          {walletDisponible && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3 }}>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#E2E8F0' }} />
              <Typography variant="caption" sx={{ color: '#94A3B8', fontWeight: 700 }}>
                o paga con
              </Typography>
              <Box sx={{ flex: 1, height: '1px', bgcolor: '#E2E8F0' }} />
            </Box>
          )}
        </Box>
      )}

      {/* Selector de método */}
      <Box sx={{ display: 'flex', gap: 1.5, mb: 3, flexWrap: 'wrap' }}>
        {METODOS.map((m) => (
          <MetodoCard
            key={m.key}
            metodo={m}
            activo={paymentMethod === m.key}
            onClick={() => setPaymentMethod && setPaymentMethod(m.key)}
          />
        ))}
      </Box>

      {paymentMethod === 'card' ? (
        <>
          <Typography variant="caption" color="#737791" sx={{ display: 'block', mb: 2 }}>
            Tu tarjeta solo se registra ahora. El cargo se realiza únicamente cuando el
            proveedor acepta tu reserva.
          </Typography>
          <StripeCard ref={stripeCardRef} />
        </>
      ) : (
        <Box sx={{ bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '14px', p: 2.5 }}>
          <Typography variant="body2" fontWeight={700} color="#0E2465" sx={{ mb: 0.5 }}>
            {paymentMethod === 'spei' ? 'Pago por transferencia SPEI' : 'Pago en efectivo en OXXO'}
          </Typography>
          <Typography variant="caption" color="#737791" sx={{ display: 'block', lineHeight: 1.6 }}>
            {paymentMethod === 'spei'
              ? 'Cuando el proveedor acepte tu reserva te daremos una CLABE y una referencia para hacer la transferencia. Tu lugar se aparta al confirmar el pago.'
              : 'Cuando el proveedor acepte tu reserva te daremos una ficha para pagar en cualquier OXXO. Tu lugar se aparta al confirmar el pago.'}
            {' '}Tendrás un plazo para pagarla (hasta 72 horas o la hora del servicio).
          </Typography>
        </Box>
      )}

      {/* Política de cancelación: el cliente la conoce ANTES de reservar */}
      <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid #F1F5F9' }}>
        <Typography variant="caption" color="#737791" sx={{ display: 'block', lineHeight: 1.6 }}>
          <strong style={{ color: '#0E2465' }}>Política de cancelación:</strong> puedes cancelar
          sin costo durante la primera hora después de reservar, o mientras falten más de
          24 horas para el servicio. Después se retiene el 30% como compensación al proveedor.
          Con menos de 3 horas ya no es posible cancelar desde la app.
        </Typography>
      </Box>
    </Box>
  );
};

export default BookingPayment;
